import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useCampaigns } from '@/hooks/useCampaigns';
import { formatRelativeDate } from '@/utils/date';
import type { Campaign } from '@/types/api';
import { NewCampaignModal } from '@/components/modals/NewCampaignModal';
import { EditCampaignDetailsModal } from '@/components/modals/EditCampaignDetailsModal';

const FILTER_TABS = ['All Campaigns', 'Active', 'Archived'];

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function campaignStatus(_c: Campaign): 'Active' | 'Draft' | 'Archived' {
  return 'Active';
}

const STATUS_BADGE: Record<string, string> = {
  Active: 'bg-primary text-on-primary',
  Draft: 'bg-stone-200 text-on-surface-variant',
  Archived: 'bg-surface-container-high text-on-surface-variant',
};

function SkeletonCard() {
  return (
    <div className="bg-surface-container-lowest rounded-2xl p-6 border border-outline-variant/10 animate-pulse">
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 rounded-xl bg-surface-container-high" />
        <div className="flex-1 space-y-2">
          <div className="h-4 w-56 bg-surface-container-high rounded" />
          <div className="h-3 w-32 bg-surface-container rounded" />
        </div>
      </div>
    </div>
  );
}

export default function CampaignsPage() {
  const [activeFilter, setActiveFilter] = useState('All Campaigns');
  const [isNewCampaignOpen, setIsNewCampaignOpen] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState<Campaign | null>(null);
  const { data, loading, error, refetch } = useCampaigns();

  const campaigns = data ?? [];

  const visible = campaigns.filter((c) => {
    if (activeFilter === 'All Campaigns') return true;
    const s = campaignStatus(c);
    if (activeFilter === 'Active') return s === 'Active' || s === 'Draft';
    if (activeFilter === 'Archived') return s === 'Archived';
    return true;
  });

  return (
    <div className="p-4 md:p-8 lg:p-12 space-y-10">
      {/* Header */}
      <div className="flex items-end justify-between gap-6 flex-wrap">
        <div className="space-y-1">
          <h1 className="text-4xl font-black tracking-tight text-on-surface font-headline">
            Campaigns
          </h1>
          <p className="text-on-surface-variant font-medium">
            Manage and organise your creative workflows.
          </p>
        </div>
        <button
          onClick={() => setIsNewCampaignOpen(true)}
          className="flex items-center gap-2 bg-primary hover:bg-primary-dim text-on-primary px-6 py-3 rounded-xl font-bold transition-all shadow-lg shadow-primary/20 active:scale-95 cursor-pointer"
        >
          <span className="material-symbols-outlined">add_circle</span>
          New Campaign
        </button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        {FILTER_TABS.map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveFilter(tab)}
            className={`px-5 py-2 rounded-full text-xs font-bold tracking-wider uppercase transition-colors cursor-pointer ${
              activeFilter === tab
                ? 'bg-primary text-on-primary shadow-md shadow-primary/10'
                : 'bg-surface-container-lowest text-on-surface-variant hover:bg-primary-container border border-outline-variant/10'
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* API error */}
      {error && (
        <div className="mb-8 p-4 rounded-xl bg-error-container text-on-error-container text-sm">
          {error}
        </div>
      )}

      {/* Timeline */}
      <div className="relative max-w-4xl">
        <div
          className="absolute left-[7px] top-4 bottom-4 w-[2px] opacity-20 hidden md:block"
          style={{
            backgroundImage: 'linear-gradient(to bottom, #8e84ad 50%, transparent 50%)',
            backgroundSize: '2px 8px',
            backgroundRepeat: 'repeat-y',
          }}
        />

        <div className="space-y-8">
          {loading &&
            [1, 2, 3].map((i) => (
              <div key={i} className="relative pl-0 md:pl-10">
                <div className="hidden md:block absolute left-0 top-1.5 w-4 h-4 rounded-full bg-surface-container-high animate-pulse" />
                <SkeletonCard />
              </div>
            ))}

          {!loading &&
            visible.map((c) => {
              const status = campaignStatus(c);
              return (
                <div key={c.id} className="relative pl-0 md:pl-10">
                  <div
                    className={`hidden md:block absolute left-0 top-1.5 w-4 h-4 rounded-full z-10 ${
                      status === 'Active'
                        ? 'bg-primary shadow-[0_0_0_6px_rgba(142,132,173,0.15)]'
                        : 'bg-surface-container-lowest border-2 border-primary'
                    }`}
                  />
                  <div className="mb-3">
                    <span className="text-[10px] font-black tracking-[0.2em] uppercase text-primary">
                      {formatRelativeDate(c.updated_at)}
                    </span>
                  </div>
                  <Link
                    to={`/campaigns/${c.id}`}
                    className="group block bg-surface-container-lowest rounded-2xl p-6 border border-outline-variant/10 hover:border-primary/20 hover:shadow-md transition-all duration-300"
                  >
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-primary-container flex items-center justify-center">
                          <span
                            className="material-symbols-outlined text-primary"
                            style={{ fontVariationSettings: "'FILL' 1" }}
                          >
                            campaign
                          </span>
                        </div>
                        <div>
                          <h3 className="text-lg font-bold text-on-surface group-hover:text-primary transition-colors font-headline">
                            {c.name}
                          </h3>
                          <div className="flex items-center gap-3 mt-1">
                            <span
                              className={`px-2 py-0.5 text-[9px] font-bold tracking-wider uppercase rounded ${STATUS_BADGE[status]}`}
                            >
                              {status}
                            </span>
                            <span className="text-xs text-on-surface-variant font-medium">
                              Updated {formatRelativeDate(c.updated_at)}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-8 px-4 md:border-l border-outline-variant/10">
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setEditingCampaign(c);
                          }}
                          className="text-on-surface-variant hover:text-primary transition-colors cursor-pointer"
                        >
                          <span className="material-symbols-outlined">more_vert</span>
                        </button>
                      </div>
                    </div>
                  </Link>
                </div>
              );
            })}

          {!loading && visible.length === 0 && !error && (
            <div className="pl-10 py-16 text-center">
              <span className="material-symbols-outlined text-5xl text-primary/30">campaign</span>
              <p className="mt-4 text-on-surface-variant font-medium">No campaigns yet.</p>
              <button
                onClick={() => setIsNewCampaignOpen(true)}
                className="mt-4 text-primary font-bold text-sm hover:underline cursor-pointer"
              >
                Create your first campaign →
              </button>
            </div>
          )}
        </div>
      </div>

      <NewCampaignModal
        isOpen={isNewCampaignOpen}
        onClose={() => setIsNewCampaignOpen(false)}
        onCampaignCreated={() => refetch()}
      />
      <EditCampaignDetailsModal
        isOpen={editingCampaign != null}
        onClose={() => setEditingCampaign(null)}
        campaign={editingCampaign}
        onSaved={() => {
          refetch();
          setEditingCampaign(null);
        }}
      />
    </div>
  );
}
